import type { SerializedStyles } from "@emotion/serialize";
import { Checkbox } from "@fullscript/aviary-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Accordion, ShowMoreButton, Spacer } from "@aviary";
import type { Filter } from "@shared/components/CatalogSearch/CatalogSearch";
import { FilterTitle } from "@shared/components/CatalogSearch/Filters/shared/FilterTitle/FilterTitle";
import { useAllergensSharedQuery } from "@shared/data/queries/AllergensQuery/Allergens.query.generated";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";
import type * as Types from "@shared/types/graphqlGenerated";

import * as styles from "./Allergens.styles";

type Allergen = Pick<Types.AllergenContextualFilters, "id" | "esResultCount"> & {
  allergen: Pick<Types.Allergen, "id" | "name">;
};
type NonContextualAllergen = Pick<Types.Allergen, "id" | "name">;

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  title?: string;
  customAccordionStyle?: SerializedStyles[] | SerializedStyles;
  isContextual?: boolean;
  contextualData?: Allergen[];
}

const Allergens = ({
  isBold,
  logFilterEvent,
  title,
  customAccordionStyle,
  isContextual = false,
  contextualData,
}: Props) => {
  const { t } = useTranslation();
  const { activeFilters, setActiveFilters } = useCatalogSearch();
  const { data, loading } = useAllergensSharedQuery({
    variables: {
      input: [{ direction: "DESC", field: "SEARCH_POPULARITY" }],
    },
  });

  const showLessCutoff = 5;

  const [showAll, setShowAll] = useState(false);

  if (isContextual ? (contextualData?.length ?? 0) === 0 : loading || !data?.viewer?.allergens) {
    return null;
  }

  // new contextual filters that include result count
  const allergenFilters: Allergen[] = contextualData ?? [];

  const renderAllergenLabel = (allergenWithCount: Allergen) => {
    return `${allergenWithCount.allergen.name} (${allergenWithCount.esResultCount})`;
  };

  const renderAllergens = () =>
    allergenFilters?.map((allergenWithCount: Allergen, index) => {
      if (showAll || index < showLessCutoff) {
        return (
          <span key={allergenWithCount.allergen.id} css={styles.checkbox}>
            <Checkbox
              isFullWidth
              checked={isAllergenActive(allergenWithCount.allergen.id)}
              onChange={() =>
                handleAllergenSelect({
                  name: allergenWithCount.allergen.name,
                  id: allergenWithCount.allergen.id,
                  category: "Allergens",
                })
              }
            >
              {renderAllergenLabel(allergenWithCount)}
            </Checkbox>
          </span>
        );
      }

      return null;
    });

  const renderShowMoreButton = () => {
    if ((allergenFilters?.length ?? 0) <= showLessCutoff) {
      return null;
    }

    return (
      <>
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </>
    );
  };

  // old filters that don't include result count
  const nonContextualAllergenFilters: NonContextualAllergen[] = data?.viewer?.allergens ?? [];

  const renderNonContextualAllergenLabel = (allergen: NonContextualAllergen) => {
    return allergen.name;
  };

  const renderNonContextualAllergens = () =>
    nonContextualAllergenFilters?.map((allergen: NonContextualAllergen, index) => {
      if (showAll || index < showLessCutoff) {
        return (
          <span key={allergen.id} css={styles.checkbox}>
            <Checkbox
              isFullWidth
              checked={isAllergenActive(allergen.id)}
              onChange={() =>
                handleAllergenSelect({
                  name: allergen.name,
                  id: allergen.id,
                  category: "Allergens",
                })
              }
            >
              {renderNonContextualAllergenLabel(allergen)}
            </Checkbox>
          </span>
        );
      }

      return null;
    });

  const renderNonContextualShowMoreButton = () => {
    if ((nonContextualAllergenFilters?.length ?? 0) <= showLessCutoff) {
      return null;
    }

    return (
      <>
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </>
    );
  };

  // shared logic for both contextual and non-contextual filters
  const isAllergenActive = (id: string): boolean =>
    activeFilters.some(activeAllergen => activeAllergen.id === id);

  const handleAllergenSelect = (allergenSelected: Filter) => {
    const distanceFromTop = window?.scrollY;
    if (isAllergenActive(allergenSelected.id)) {
      setActiveFilters(activeFilters.filter(allergen => allergen.name !== allergenSelected.name));
    } else {
      logFilterEvent(allergenSelected.name, FilterType.ALLERGENS);
      setActiveFilters([...activeFilters, allergenSelected]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const activeAllergenCount = activeFilters.filter(
    active => active?.category === "Allergens"
  )?.length;

  return (
    <Accordion
      css={customAccordionStyle}
      coreTriggerContent={
        <FilterTitle
          title={title || t(l.sharedCatalog.filter.AllergenIndicators)}
          count={activeAllergenCount}
          isBadge
          isSecondaryWeight={isBold}
        />
      }
    >
      <div css={styles.checkboxList}>
        {isContextual ? renderAllergens() : renderNonContextualAllergens()}
        {isContextual ? renderShowMoreButton() : renderNonContextualShowMoreButton()}
      </div>
    </Accordion>
  );
};

export { Allergens };
